import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { ZoomTransform, max, scaleLinear } from 'd3';
import partition from 'lodash/partition';

import theme from '@src/theme';

import Button from '@components/common/Button';

import Area from '@pages/Vault/LiquidityChart/components/Area';

import { Size, Variant } from '@constants/common/ui.constants';

import { ChartEntry, Dimensions, Margins, ZoomLevels } from '../../types/types';
import AxisBottom from '../AxisBottom';
import Brush from '../Brush';
import Line from '../Line';
import Zoom, { ZoomOverlay } from '../Zoom';
import useStyles from './styles.module';

const xAccessor = (d: ChartEntry) => Number(d.price);
const yAccessor = (d: ChartEntry) => Number(d.liquidityUsd);

export interface Props {
  // to distringuish between multiple charts in the DOM
  id?: string;
  data: {
    series: ChartEntry[];
    current: number;
  };
  dimensions: Dimensions;
  margins: Margins;
  isResetDisabled: boolean;
  interactive?: boolean;
  brushLabels: (d: 'w' | 'e', x: number) => string;
  brushDomain: [number, number] | undefined;
  onBrushDomainChange: (domain: [number, number], mode?: string) => void;
  zoomLevels: ZoomLevels;
  showZoomButtons?: boolean;
  onResetState: () => void;
}

const Chart: FC<Props> = ({
  id = 'liquidityChartRangeInput',
  data: { series, current },
  dimensions: { width, height },
  isResetDisabled,
  margins,
  interactive = true,
  brushDomain,
  brushLabels,
  onBrushDomainChange,
  zoomLevels,
  showZoomButtons = true,
  onResetState,
}) => {
  const { classes } = useStyles();

  const zoomRef = useRef<SVGRectElement | null>(null);
  const [zoom, setZoom] = useState<ZoomTransform | null>(null);
  const [isReady, setIsReady] = useState(false);
  const didInitBrush = useRef(false);

  const [innerHeight, innerWidth] = useMemo(
    () => [
      height - margins.top - margins.bottom,
      width - margins.left - margins.right,
    ],
    [width, height, margins],
  );

  const { xScale, yScale } = useMemo(() => {
    const scales = {
      xScale: scaleLinear()
        .domain([
          current * zoomLevels.initialMin,
          current * zoomLevels.initialMax,
        ] as number[])
        .range([0, innerWidth]),
      yScale: scaleLinear()
        .domain([0, max(series, yAccessor)] as number[])
        .range([innerHeight, 0]),
    };

    if (zoom) {
      const newXscale = zoom.rescaleX(scales.xScale);
      scales.xScale.domain(newXscale.domain());
    }

    return scales;
  }, [
    current,
    zoomLevels.initialMin,
    zoomLevels.initialMax,
    innerWidth,
    series,
    innerHeight,
    zoom,
  ]);

  useEffect(() => {
    // reset zoom as necessary
    setZoom(null);
  }, [zoomLevels]);

  useEffect(() => {
    if (!didInitBrush.current && brushDomain === undefined) {
      didInitBrush.current = true;
      const domain = xScale.domain() as [number, number];
      console.log('[Chart] Initial brush set');
      onBrushDomainChange(domain, 'initial');
    }
  }, [brushDomain, xScale]);

  const [leftSeries, rightSeries] = useMemo(() => {
    const isHighToLow = series[0]?.price > series[series.length - 1]?.price;
    let [left, right] = partition(series, (d) =>
      isHighToLow ? +xAccessor(d) < current : +xAccessor(d) > current,
    );

    if (right.length && right[right.length - 1]) {
      if (right[right.length - 1].price !== current) {
        right = [
          ...right,
          {
            liquidityUsd: right[right.length - 1].liquidityUsd,
            price: current,
          },
        ];
      }
      left = [
        {
          liquidityUsd: right[right.length - 1].liquidityUsd,
          price: current,
        },
        ...left,
      ];
    }

    return [left, right];
  }, [current, series]);

  useEffect(() => {
    if (zoomRef.current) {
      setIsReady(true);
    }
  }, [zoomRef.current]);

  return (
    <>
      <Button
        className={classes.btn}
        variant={Variant.Secondary}
        size={Size.Small}
        onClick={onResetState}
        disabled={isResetDisabled}
      >
        Current range
      </Button>
      {showZoomButtons && isReady && (
        <Zoom
          svg={zoomRef.current}
          setZoom={setZoom}
          width={innerWidth}
          height={height}
          // resetBrush={() => {
          //   onBrushDomainChange(
          //     [
          //       current * zoomLevels.initialMin,
          //       current * zoomLevels.initialMax,
          //     ] as [number, number],
          //     'reset',
          //   );
          // }}
          showResetButton={false}
          zoomLevels={zoomLevels}
        />
      )}
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="green-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.accent.a5} stopOpacity={1} />
            <stop offset="100%" stopColor={theme.accent.a5} stopOpacity={0.2} />
          </linearGradient>
          <linearGradient id="red-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={theme.accent.a3} stopOpacity={1} />
            <stop offset="100%" stopColor={theme.accent.a3} stopOpacity={0.2} />
          </linearGradient>
        </defs>
        <defs>
          <clipPath id={`${id}-chart-clip`}>
            <rect x="0" y="0" width={innerWidth} height={height} />
          </clipPath>

          {brushDomain && (
            <mask id={`${id}-chart-area-mask`}>
              <rect
                fill="white"
                x={xScale(brushDomain[0])}
                y="0"
                width={xScale(brushDomain[1]) - xScale(brushDomain[0])}
                height={innerHeight}
              />
            </mask>
          )}
        </defs>

        <g transform={`translate(${margins.left},${margins.top})`}>
          <g clipPath={`url(#${id}-chart-clip)`}>
            <Area
              series={leftSeries}
              xScale={xScale}
              yScale={yScale}
              xValue={xAccessor}
              yValue={yAccessor}
              opacity={0.5}
              fill="url(#red-gradient)"
            />
            <Area
              series={rightSeries}
              xScale={xScale}
              yScale={yScale}
              xValue={xAccessor}
              yValue={yAccessor}
              opacity={0.5}
              fill="url(#green-gradient)"
            />

            {brushDomain && (
              // duplicate area chart with mask for selected area
              <g mask={`url(#${id}-chart-area-mask)`}>
                <Area
                  opacity={0.1}
                  series={series}
                  xScale={xScale}
                  yScale={yScale}
                  xValue={xAccessor}
                  yValue={yAccessor}
                  fill="white"
                />
              </g>
            )}

            <Line value={current} xScale={xScale} innerHeight={innerHeight} />

            <AxisBottom xScale={xScale} innerHeight={innerHeight} />
          </g>

          <ZoomOverlay width={innerWidth} height={height} ref={zoomRef} />

          <Brush
            id={id}
            xScale={xScale}
            interactive={interactive}
            brushLabelValue={brushLabels}
            brushExtent={brushDomain ?? (xScale.domain() as [number, number])}
            innerWidth={innerWidth}
            innerHeight={innerHeight}
            setBrushExtent={onBrushDomainChange}
          />
        </g>
      </svg>
    </>
  );
};

export default Chart;
